/**
 * ECharts wrapper with one shared theme.
 *
 * Every chart in the product goes through here so axes, grids, tooltips and the
 * categorical palette are consistent — a dashboard where two charts disagree on
 * what blue means is harder to read than one with fewer charts.
 */
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

/** Categorical palette: distinguishable, and none of them the accent petrol. */
export const SERIES_COLORS = [
  "#0B5D57", "#C2670A", "#2563C9", "#7A5AF8", "#0E9AA0",
  "#2F6B3C", "#B4690E", "#9E3B2E", "#4F46E5", "#0B7A5E",
];

const BASE: EChartsOption = {
  color: SERIES_COLORS,
  textStyle: { fontFamily: "Archivo, Helvetica Neue, Arial, sans-serif", fontSize: 12 },
  grid: { left: 8, right: 12, top: 28, bottom: 8, containLabel: true },
  tooltip: {
    trigger: "axis",
    backgroundColor: "#16211F",
    borderWidth: 0,
    textStyle: { color: "#F6F8F7", fontSize: 12 },
    axisPointer: { type: "line", lineStyle: { color: "#C7D2CE" } },
  },
  legend: {
    top: 0, right: 0, icon: "roundRect", itemWidth: 9, itemHeight: 9,
    textStyle: { color: "#5D6E6A", fontSize: 11 },
  },
};

const AXIS_LINE = { lineStyle: { color: "#DBE3E0" } };
const SPLIT_LINE = { lineStyle: { color: "#EEF2F1", type: "dashed" as const } };

export function applyAxisDefaults(option: EChartsOption): EChartsOption {
  const withAxis = { ...option };
  if (withAxis.xAxis) {
    const axes = Array.isArray(withAxis.xAxis) ? withAxis.xAxis : [withAxis.xAxis];
    withAxis.xAxis = axes.map((axis) => ({
      axisLine: AXIS_LINE, axisTick: { show: false },
      axisLabel: { color: "#5D6E6A", fontSize: 11 },
      splitLine: { show: false }, ...axis,
    })) as EChartsOption["xAxis"];
  }
  if (withAxis.yAxis) {
    const axes = Array.isArray(withAxis.yAxis) ? withAxis.yAxis : [withAxis.yAxis];
    withAxis.yAxis = axes.map((axis) => ({
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { color: "#5D6E6A", fontSize: 11 },
      splitLine: SPLIT_LINE, ...axis,
    })) as EChartsOption["yAxis"];
  }
  return withAxis;
}

export interface ChartProps {
  option: EChartsOption;
  height?: number;
  onEvents?: Record<string, (params: unknown) => void>;
  loading?: boolean;
  className?: string;
}

export function Chart({ option, height = 280, onEvents, loading, className }: ChartProps) {
  const merged = useMemo(() => applyAxisDefaults({ ...BASE, ...option }), [option]);
  if (loading) {
    return <div className="skeleton" style={{ height }} />;
  }
  return (
    <ReactECharts
      option={merged}
      style={{ height }}
      className={className}
      onEvents={onEvents}
      notMerge
      lazyUpdate
    />
  );
}
